import { isSignal, WritableSignal } from '@angular/core';
import Collection from '@arcgis/core/core/Collection';
import {
  ButtonParams,
  ExtendedMap,
  JepLayer,
  LayerConfiguration,
  LegendParams,
  Plugin,
  PluginID,
  PluginMap,
  PluginConfigurationRecord
} from '@jeppesen-foreflight/jmkw-types';
import startCase from 'lodash-es/startCase';
import { Subject } from 'rxjs';
import { CheckboxNode } from '@jeppesen-foreflight/jmkw-toolkit';

export function pluginsToCheckboxNodeMap(
  plugins?: Plugin[] | PluginMap
): ExtendedMap<CheckboxNode> {
  if (!plugins) {
    return new ExtendedMap<CheckboxNode>();
  }

  const pluginsArray =
    plugins instanceof PluginMap ? Array.from(plugins.values()) : plugins;

  return pluginsArray.reduce((extMap, plugin, index) => {
    const node = new CheckboxNode({
      id: plugin.id,
      label: plugin.layer?.label ?? startCase(plugin.id),
      order: index,
      expanded: true,
      selected: !!plugin.layer?.visible,
      automationKey: `${plugin.id}-checkbox`,
      onChange: (selected) => {
        if (plugin.layer) {
          plugin.layer.jepTools.toggleVisibility(selected);
        }
      },
      children: plugin.children
        ? pluginsToCheckboxNodeMap(plugin.children)
        : new ExtendedMap<CheckboxNode>()
    });

    if (node.children.size) {
      if ([...node.children.values()].every((child) => child.selected)) {
        node.selected = true;
      } else if ([...node.children.values()].some((child) => child.selected)) {
        node.indeterminate = true;
      }
    }
    extMap.set(plugin.id, node);
    return extMap;
  }, new ExtendedMap<CheckboxNode>());
}

export function pluginArrayToMap(plugins: Plugin[] | PluginMap): PluginMap {
  if (plugins instanceof PluginMap) {
    return plugins;
  }

  const pluginMap = new PluginMap();

  for (const plugin of plugins) {
    if (pluginMap.has(plugin.id)) {
      throw new Error(
        'Each plugin must have a unique id. Found ' +
          plugin.id +
          ' more than once.'
      );
    } else {
      pluginMap.set(plugin.id, plugin);
    }
  }

  return pluginMap;
}

export function pluginToChildren(plugin: Plugin): [PluginID, Plugin] {
  return [plugin.id, plugin];
}

/**
 * Generic function to apply configuration properties from a configuration object to the target object,
 * while explicitly skipping properties whose keys are included in the `exclusions` array.
 *
 * @param target The target object on which to apply configurations.
 * @param config The configuration object containing key-value pairs.
 * @param exclusions An array of keys that should not be applied.
 */
export function applyConfigurations<T>(
  target: T,
  config: Partial<T>,
  exclusions: string[]
): void {
  try {
    Object.entries(config).forEach(([key, value]) => {
      if (!exclusions.includes(String(key))) {
        setValue(target, key as keyof T, value as T[keyof T]);
      }
    });
  } catch (error) {
    console.error('Error applying configuration:', error);
  }
}

/**
 * Applies button configurations by skipping keys 'id' and 'buttons'.
 *
 * @param button The target button object.
 * @param buttonConfig The configuration to apply.
 */
export function applyButtonConfigurations(
  button: ButtonParams,
  buttonConfig?: Partial<ButtonParams>
): void {
  if (buttonConfig) {
    applyConfigurations<ButtonParams>(button, buttonConfig, ['id']);
  }
}

/**
 * Applies legend configurations by skipping keys 'id' and 'legends'.
 *
 * @param legend The target legend object.
 * @param legendConfig The configuration to apply.
 */
export function applyLegendConfigurations(
  legend: LegendParams,
  legendConfig?: Partial<LegendParams>
): void {
  if (legendConfig) {
    applyConfigurations<LegendParams>(legend, legendConfig, ['id']);
  }
}

/**
 * Applies layer configurations by skipping keys 'id' and 'layers', and
 * recursively applies configurations to sublayers if present.
 *
 * @param layer The target layer.
 * @param layerConfiguration The configuration object to apply.
 */
export function applyLayerConfigurations(
  layer: JepLayer,
  layerConfiguration?: LayerConfiguration,
  configs?: PluginConfigurationRecord
): void {
  if (!layerConfiguration) return;

  // Apply the configuration to the current layer.
  applyConfigurations<JepLayer>(layer, layerConfiguration, ['id', 'layers']);

  // If the layer includes sublayers, recursively apply configurations.
  if (
    'layers' in layer &&
    layer.layers instanceof Collection &&
    typeof layer.layers.forEach === 'function'
  ) {
    layer.layers.forEach((sublayer) => {
      if (sublayer && 'id' in sublayer) {
        let sublayerConfig: LayerConfiguration | undefined;

        //Sublayer exists in layerConfiguration
        if (
          layerConfiguration &&
          layerConfiguration['layers'] &&
          layerConfiguration['layers'][sublayer.id]
        ) {
          sublayerConfig = (layerConfiguration as any).layers[sublayer.id];
        }

        //Sublayer exists, but set in baseline configs
        else if (configs && configs[sublayer.id]) {
          sublayerConfig = configs[sublayer.id]?.layer;
        }

        if (sublayerConfig) {
          applyLayerConfigurations(sublayer, sublayerConfig, configs);
        }
      }
    });
  }
}

/**
 * Sets a value on the target object for a given key. It checks if the property is a
 * Subject or a WritableSignal and applies the update accordingly. If neither, it performs
 * a standard property assignment.
 *
 * @param obj The target object.
 * @param key The key for the property to set.
 * @param value The new value to assign.
 */
export function setValue<T, K extends keyof T>(
  obj: T,
  key: K,
  value: T[K]
): void {
  try {
    if (obj[key] instanceof Subject) {
      // If the property is a Subject, emit the new value.
      (obj[key] as Subject<T[K]>).next(value);
    } else if (isSignal(obj[key])) {
      // If the property is a WritableSignal, set the new value.
      const signal = obj[key] as unknown as WritableSignal<T[K]>;
      if (typeof signal.set === 'function') {
        signal.set(value);
      } else {
        console.warn(`Invalid signal for key: ${String(key)}`);
      }
    } else {
      // Direct assignment for standard properties.
      obj[key] = value;
    }
  } catch (error) {
    console.error(`Error setting value for key ${String(key)}:`, error);
  }
}
